import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Avatar, Box, Card, CardContent, Popover, Typography, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { ConnectionInfo, ConnectionType } from "@/types/types.ts";
import { fetchConnectionInfo } from "@/services/api/user.ts";
import Image from "next/image";
import { Clock, MailIcon, MapPin } from "lucide-react";
import styles from "./ConnectionInfoPopover.module.scss";
import EllipsisTextLine from "@/common/components/Text/EllipsisTextLine.tsx";
import { debounce } from 'lodash';

interface ConnectionInfoPopoverProps {
  debounceFetchTime?: number;
  connectionType: ConnectionType;
  connectionId?: string;
  children: React.ReactElement;
  debug?: boolean;
}

export const ConnectionInfoPopover: React.FC<ConnectionInfoPopoverProps> = (
  {
    debounceFetchTime,
    connectionType,
    connectionId,
    children,
    debug = false
  }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [connectionInfo, setConnectionInfo] = useState<ConnectionInfo | null>(null);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const fetchData = useCallback(async () => {
    if (!connectionId) return;

    try {
      const response = await fetchConnectionInfo(connectionId, connectionType);
      if (response.ok) {
        const result = await response.json();
        setConnectionInfo(result);
      }
    } catch (error) {
      console.error('Failed to fetch connection info:', error);
    }
  }, [
    connectionId,
    connectionType
  ]);

  const fetchInfo = useMemo(
    () => debounce(fetchData, debounceFetchTime ?? 0),
    [
      fetchData,
      debounceFetchTime
    ]
  );

  const closePopover = useCallback(() => {
    if (debug) return;

    setAnchorEl(null);
    setConnectionInfo(null);
    fetchInfo.cancel?.();
  }, [
    debug,
    fetchInfo
  ]);

  const handleContainerMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (isMobile) return;
      fetchInfo()
      setAnchorEl(event.currentTarget);
    },
    [
      fetchInfo,
      isMobile
    ]
  );

  const handleContainerMouseLeave = useCallback(
    (event: React.MouseEvent) => {
      const relatedTarget = event.relatedTarget as HTMLElement;
      const isWithinContainer = containerRef.current?.contains(relatedTarget);
      const isWithinPopover = popoverRef.current?.contains(relatedTarget);

      if (!isWithinContainer && !isWithinPopover) {
        closePopover();
      }
    },
    [closePopover]
  );

  const handlePopoverMouseLeave = useCallback(
    (event: React.MouseEvent) => {
      if (debug) return;

      const relatedTarget = event.relatedTarget as HTMLElement;
      const isWithinContainer = containerRef.current?.contains(relatedTarget);
      const isWithinPopover = popoverRef.current?.contains(relatedTarget);

      if (!isWithinContainer && !isWithinPopover) {
        closePopover();
      }
    },
    [
      closePopover,
      debug
    ]
  );

  const handleTouch = useCallback((event: React.TouchEvent<HTMLElement>) => {
    if (!isMobile) return;
    setAnchorEl(event.currentTarget);
    fetchInfo();
  }, [
    fetchInfo,
    isMobile
  ]);

  const popoverContent = useMemo(() => {
    if (!connectionInfo) {
      return null;
    }

    return (
      <Card
        ref={popoverRef}
        className={styles.MainCard}
      >
        <CardContent className={styles.CardContent}>
          <Box className={styles.HeaderContainer}>
            <Avatar
              className={styles.Avatar}
            >
              {connectionInfo.avatar ? (
                <Image
                  src={connectionInfo.avatar}
                  alt={connectionInfo.name}
                  width={50}
                  height={50}
                  style={{ borderRadius: "50%" }}
                />
              ) : (
                <Typography variant="h4" fontWeight="bold" fontSize="1.6rem">
                  {connectionInfo.name.charAt(0).toUpperCase()}
                </Typography>
              )}
            </Avatar>
            <Box className={styles.HeaderTextContainer}>
              <Typography variant="h6" component="div">
                {connectionInfo.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {connectionType === ConnectionType.USER ? "User" : "Band"}
              </Typography>
            </Box>
          </Box>
          <Box className={styles.InfoContainer}>
            {connectionInfo.emailAddress && (
              <Box className={styles.InfoRow}>
                <MailIcon size={16} />
                <EllipsisTextLine
                  maxWidth={150}
                  variant="body2"
                  content={
                    <a href={`mailto: ${connectionInfo.emailAddress}`}>{connectionInfo.emailAddress}</a>
                  }
                />
              </Box>
            )}
            {connectionInfo.lastActive && (
              <Box className={styles.InfoRow}>
                <Clock size={16} />
                <Typography variant="body2">Last active: {connectionInfo.lastActive}</Typography>
              </Box>
            )}
            {connectionInfo.location && (
              <Box className={styles.InfoRow}>
                <MapPin size={16} />
                <Typography variant="body2">{connectionInfo.location}</Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  }, [
    connectionInfo,
    connectionType
  ]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        display: 'inline-block',
      }}
      onMouseEnter={handleContainerMouseEnter}
      onMouseLeave={handleContainerMouseLeave}
      onTouchStart={handleTouch}
    >
      {children}
      <Popover
        open={Boolean(anchorEl) || debug}
        anchorEl={anchorEl}
        container={document.body}
        onClose={closePopover}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        sx={{
          pointerEvents: 'none',
        }}
        slotProps={{
          paper: {
            onMouseLeave: handlePopoverMouseLeave,
            sx: {
              borderRadius: 3,
              pointerEvents: 'auto',
              boxShadow: 3,
              overflow: 'hidden',
              backgroundColor: 'transparent',
            }
          }
        }}
      >
        {popoverContent}
      </Popover>
    </Box>
  );
};
